import { FitScreenOutlined, OpenInFullOutlined } from '@mui/icons-material';
import { Grid, IconButton, Slider, Tooltip } from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useControls, useTransformContext } from 'react-zoom-pan-pinch';

import {
  FIT_TO_SCREEN_BUTTON_DATA_TEST_ID,
  MAX_SCALE,
  MIN_SCALE,
} from '../constants';
import { ScaleBarProps } from '../ScaleContainer.interface';
import { fitContentToWrapper } from '../utils/fitContentToWrapper';
import { Wrapper } from './ScaleBar.styled';

export const ScaleBar = ({
  wrapperRef,
  contentRef,
  watchValue,
  showScaleControls,
  scaleBarStyles,
}: ScaleBarProps) => {
  const [scale, setScale] = useState(0.5);
  const [isFullscreenTransitioning, setIsFullscreenTransitioning] =
    useState(false);

  const { centerView } = useControls();
  const transformCtx = useTransformContext();

  const watchedValue = useRef(null);

  const fitContentToWrapperScale =
    fitContentToWrapper({
      wrapperRef,
      contentRef,
      options: {
        isScaleControlsHidden: !showScaleControls,
      },
    }) || MIN_SCALE;

  const isContentSmallerThanWrapper = fitContentToWrapperScale > 1;
  const contentMinScale = isContentSmallerThanWrapper
    ? 1
    : fitContentToWrapperScale;
  const contentMaxScale = isContentSmallerThanWrapper
    ? fitContentToWrapperScale + 1
    : MAX_SCALE;

  const fitContentWithCenterView = useCallback(
    (skipAnimation?: boolean) => {
      const fitContentScale = fitContentToWrapper({
        wrapperRef,
        contentRef,
        callback: (fitContentScale) =>
          centerView(
            fitContentScale,
            skipAnimation || !showScaleControls ? 0 : undefined,
          ),
        options: {
          isScaleControlsHidden: !showScaleControls,
        },
      });
      setIsFullscreenTransitioning(false);

      return fitContentScale;
    },
    [wrapperRef, contentRef, centerView, showScaleControls],
  );

  const debounceFitContentToWrapper = debounce(
    () => fitContentWithCenterView(),
    100,
  );

  const handleFullScreen = (element: HTMLDivElement | null | undefined) => {
    if (document && element) {
      if (!document.fullscreenElement) {
        if (element.requestFullscreen) {
          setIsFullscreenTransitioning(true);
          element.requestFullscreen();
          debounceFitContentToWrapper();
        }
      } else if (document.exitFullscreen) {
        setIsFullscreenTransitioning(true);
        document.exitFullscreen();
        debounceFitContentToWrapper();
      }
    }
  };

  const onScaleChange = (newScale: number) => {
    centerView(newScale);
  };

  useEffect(() => {
    // Access and update scale state
    transformCtx.onChange(({ state }) => setScale(state.scale));
    // Update container min scale accepted
    transformCtx.update({
      minScale: contentMinScale,
      maxScale: contentMaxScale,
      disabled: !showScaleControls,
      wheel: {
        disabled: !showScaleControls,
      },
    });
    // Handle fit content to wrapper when content loaded
    if (watchedValue.current !== watchValue) {
      watchedValue.current = watchValue;
      const skipAnimation = true;
      fitContentWithCenterView(skipAnimation);
    }
  }, [
    transformCtx,
    contentMinScale,
    contentMaxScale,
    fitContentWithCenterView,
    watchValue,
    showScaleControls,
  ]);

  return (
    <Wrapper
      contentRef={contentRef}
      sx={{
        display: !showScaleControls && 'none',
        ...scaleBarStyles,
      }}>
      <Grid
        container
        justifyContent="end"
        alignItems="center"
        wrap="nowrap"
        gap={1}>
        <Grid item xs={1}>
          <Slider
            size="small"
            color="primary"
            valueLabelDisplay="auto"
            disabled={isFullscreenTransitioning}
            onChange={(_, value) => onScaleChange(value as number)}
            value={scale}
            min={contentMinScale}
            max={contentMaxScale}
            step={0.01}
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          />
        </Grid>

        <Tooltip title={'fit to screen'}>
          <IconButton
            size="small"
            color="primary"
            disabled={isFullscreenTransitioning}
            onClick={() => fitContentWithCenterView()}
            data-testid={FIT_TO_SCREEN_BUTTON_DATA_TEST_ID}>
            <FitScreenOutlined />
          </IconButton>
        </Tooltip>

        <Tooltip title={'full screen'}>
          <IconButton
            size="small"
            color="primary"
            disabled={isFullscreenTransitioning}
            onClick={() => {
              handleFullScreen(wrapperRef?.current);
            }}>
            <OpenInFullOutlined />
          </IconButton>
        </Tooltip>
      </Grid>
    </Wrapper>
  );
};
